import { useEffect, useRef } from "react";
import { Annotation, EditorState, type Extension } from "@codemirror/state";
import { EditorView, drawSelection, highlightActiveLine, highlightActiveLineGutter, keymap, lineNumbers, placeholder as cmPlaceholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { bracketMatching, defaultHighlightStyle, foldGutter, foldKeymap, indentOnInput, syntaxHighlighting } from "@codemirror/language";
import { json } from "@codemirror/lang-json";
import { javascript } from "@codemirror/lang-javascript";
import { cn } from "@ncp/ui";

export interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  /** `tsx` covers TypeScript, JavaScript and JSX. */
  language?: "json" | "text" | "tsx";
  /** Puts the cursor on this line (1-based) and scrolls to it; changing it moves again. */
  focusLine?: number | null;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  /** Height in CSS units; the editor scrolls inside. */
  height?: string;
  /** Extra CodeMirror extensions (e.g. a linter). */
  extensions?: Extension[];
}

/** Marks a document replacement coming from the `value` prop, so it is not reported back as an edit (a stale echo would undo newer input). */
const External = Annotation.define<boolean>();

const theme = EditorView.theme({
  "&": { fontSize: "12px", backgroundColor: "transparent" },
  ".cm-content": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", padding: "8px 0" },
  ".cm-gutters": { backgroundColor: "var(--color-sand-50)", color: "var(--color-sand-500)", border: "none", borderInlineEnd: "1px solid var(--color-sand-200)" },
  ".cm-activeLineGutter": { backgroundColor: "var(--color-sand-100)" },
  ".cm-activeLine": { backgroundColor: "color-mix(in oklab, var(--color-gold-100) 35%, transparent)" },
  "&.cm-focused": { outline: "none" },
  ".cm-scroller": { overflow: "auto" },
});

/** JSON error surfaced under the editor without the full lint gutter (keeps the builder compact). */
export function jsonError(text: string): string | null {
  if (!text.trim()) return null;
  try { JSON.parse(text); return null; } catch (e) { return (e as Error).message; }
}

/**
 * CodeMirror 6 editor for JSON (schemas, UI schemas, JsonLogic) and plain text. Always LTR; the console chrome around it stays bilingual.
 * Controlled: external `value` changes replace the document when they differ from the current text.
 */
export function CodeEditor({ value, onChange, language = "json", focusLine, readOnly, placeholder, className, height = "320px", extensions = [] }: CodeEditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!host.current) return;
    const state = EditorState.create({
      doc: value,
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightActiveLine(),
        history(),
        drawSelection(),
        foldGutter(),
        indentOnInput(),
        bracketMatching(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        keymap.of([...defaultKeymap, ...historyKeymap, ...foldKeymap, indentWithTab]),
        theme,
        EditorView.lineWrapping,
        EditorState.readOnly.of(!!readOnly),
        EditorView.editable.of(!readOnly),
        ...(language === "json" ? [json()] : language === "tsx" ? [javascript({ jsx: true, typescript: true })] : []),
        ...(placeholder ? [cmPlaceholder(placeholder)] : []),
        EditorView.updateListener.of((u) => { if (u.docChanged && !u.transactions.some((tr) => tr.annotation(External))) onChangeRef.current?.(u.state.doc.toString()); }),
        ...extensions,
      ],
    });
    const v = new EditorView({ state, parent: host.current });
    view.current = v;
    return () => { v.destroy(); view.current = null; };
    // Recreate only when structural props change; value sync is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, readOnly, placeholder]);

  useEffect(() => {
    const v = view.current;
    if (!v) return;
    const current = v.state.doc.toString();
    if (current !== value) v.dispatch({ changes: { from: 0, to: current.length, insert: value }, annotations: External.of(true) });
  }, [value]);

  // The line is applied once, as soon as the document has text (a file may arrive after the request).
  const pendingLine = useRef<number | null>(null);
  useEffect(() => { pendingLine.current = focusLine ?? null; }, [focusLine]);
  useEffect(() => {
    const v = view.current;
    const target = pendingLine.current;
    if (!v || !target || v.state.doc.length === 0) return;
    pendingLine.current = null;
    const line = v.state.doc.line(Math.min(Math.max(1, target), v.state.doc.lines));
    v.dispatch({ selection: { anchor: line.from, head: line.to }, effects: EditorView.scrollIntoView(line.from, { y: "center" }) });
    v.focus();
  }, [focusLine, value]);

  return <div ref={host} dir="ltr" className={cn("overflow-hidden rounded-xl border border-sand-200 bg-white text-start [&_.cm-editor]:h-full", className)} style={{ height }} />;
}
